import {
    AuxiliaryEnginesV3BM,
    BoilerV3BM,
    CargoHeatingCirculatingPumpsV3BM,
    EngineConfigurationV3BM,
    EngineDrivenHydraulicPowerPacksV3BM,
    FreshAndSeaWaterCleaningPumpV3BM,
    HeatingSystemsConfigurationV3BM,
    HydraulicPowerPacksV3BM,
    InertGasPlantV3BM,
    MainEngineV3BM,
    TankCleanerHeaterV3BM,
    VesselConfigurationV3BM,
} from '../../types/BusinessModel/Vessel/VesselConfigurationV3BM';
import {
    AuxiliaryEnginesV3,
    BoilerV3,
    CargoHeatingCirculatingPumpsV3,
    EngineConfigurationV3,
    EngineDrivenHydraulicPowerPacksV3,
    FreshAndSeaWaterCleaningPumpV3,
    HeatingSystemsConfigurationV3,
    HydraulicPowerPacksV3,
    InertGasPlantV3,
    MainEngineV3,
    TankCleanerHeaterV3,
    VesselConfigurationV3,
} from '../../types/ViewModel/Vessel/VesselConfigurationV3';
import { AuxiliaryEngineTypeFactory } from './AuxiliaryEngineTypeFactory';

export class EngineDrivenHydraulicPowerPacksV3Factory {
    public create(data: EngineDrivenHydraulicPowerPacksV3BM): EngineDrivenHydraulicPowerPacksV3 {
        return new EngineDrivenHydraulicPowerPacksV3(data.type, data.maker, data.maximumCapacity, data.fuelType, data.numberOfPowerPacks);
    }
}

export class HydraulicPowerPacksV3Factory {
    public create(data: HydraulicPowerPacksV3BM): HydraulicPowerPacksV3 {
        return new HydraulicPowerPacksV3(data.type, data.maker, data.maximumCapacity, data.numberOfPowerPacks);
    }
}

export class BoilerV3Factory {
    public create(data: BoilerV3BM): BoilerV3 {
        return new BoilerV3(data.boilerType, data.type, data.maker, data.maximumSteamProduction, data.maximumFuelConsumption, data.canUseHfo);
    }

    public createList(data: BoilerV3BM[]): BoilerV3[] {
        return data.map((h) => this.create(h));
    }
}

export class FreshAndSeaWaterCleaningPumpV3Factory {
    public create(data: FreshAndSeaWaterCleaningPumpV3BM): FreshAndSeaWaterCleaningPumpV3 {
        return new FreshAndSeaWaterCleaningPumpV3(data.type, data.maker, data.maximumCapacity, data.pumpTypeEnum, data.numberOfPumps);
    }
}

export class CargoHeatingCirculatingPumpsV3Factory {
    public create(data: CargoHeatingCirculatingPumpsV3BM): CargoHeatingCirculatingPumpsV3 {
        return new CargoHeatingCirculatingPumpsV3(data.type, data.maker, data.maximumCapacity, data.pumpTypeEnum, data.numberOfPumps);
    }
}

export class AuxiliaryEnginesV3Factory {
    private auxiliaryEngineTypeFactory = new AuxiliaryEngineTypeFactory();

    public create(data: AuxiliaryEnginesV3BM): AuxiliaryEnginesV3 {
        return new AuxiliaryEnginesV3(
            this.auxiliaryEngineTypeFactory.create(data.auxiliaryEngineType),
            data.canUseHfo,
            data.maximumLoad,
            data.numberOfEngines,
            data.id
        );
    }

    public createList(data: AuxiliaryEnginesV3BM[]): AuxiliaryEnginesV3[] {
        return data.map((h) => this.create(h));
    }
}

export class MainEngineV3Factory {
    public create(data: MainEngineV3BM): MainEngineV3 {
        return new MainEngineV3(
            data.loadAt100Percent,
            data.rpmAt100Percent,
            data.bore,
            data.stroke,
            data.numberOfCylinders,
            data.numberOfStrokes,
            data.cylinderConstant,
            data.type,
            data.maker
        );
    }
}

export class HeatingSystemsConfigurationV3Factory {
    public create(data: HeatingSystemsConfigurationV3BM): HeatingSystemsConfigurationV3 {
        return new HeatingSystemsConfigurationV3(data.tankCleanerHeater, data.inertGasPlant, data.cargoHeatingType);
    }
}

export class TankCleanerHeaterV3Factory {
    public create(data: TankCleanerHeaterV3BM): TankCleanerHeaterV3 {
        return new TankCleanerHeaterV3(data.numberOfHeaters);
    }
}

export class InertGasPlantV3Factory {
    public create(data: InertGasPlantV3BM): InertGasPlantV3 {
        return new InertGasPlantV3(data.type, data.maker, data.maximumCapacity, data.numberOfGenerators, data.inertGasType);
    }
}

export class EngineConfigurationV3Factory {
    private mainEngineV3Factory = new MainEngineV3Factory();
    private auxiliaryEnginesV3Factory = new AuxiliaryEnginesV3Factory();
    private boilerV3Factory = new BoilerV3Factory();
    private freshAndSeaWaterCleaningPumpV3Factory = new FreshAndSeaWaterCleaningPumpV3Factory();
    private cargoHeatingCirculatingPumpsV3Factory = new CargoHeatingCirculatingPumpsV3Factory();
    private hydraulicPowerPacksV3Factory = new HydraulicPowerPacksV3Factory();
    private engineDrivenHydraulicPowerPacksV3Factory = new EngineDrivenHydraulicPowerPacksV3Factory();

    public create(data: EngineConfigurationV3BM): EngineConfigurationV3 {
        return new EngineConfigurationV3(
            this.mainEngineV3Factory.create(data.mainEngine),
            this.auxiliaryEnginesV3Factory.createList(data.auxiliaryEnginesList),
            this.boilerV3Factory.createList(data.boilers),
            this.freshAndSeaWaterCleaningPumpV3Factory.create(data.freshAndSeaWaterCleaningPump),
            this.cargoHeatingCirculatingPumpsV3Factory.create(data.cargoHeatingCirculatingPumps),
            this.hydraulicPowerPacksV3Factory.create(data.hydraulicPowerPacks),
            this.engineDrivenHydraulicPowerPacksV3Factory.create(data.engineDrivenHydraulicPowerPacks),
            data.isEconomizerEquippedWithSootBlower,
            data.shaftMaximumLoad
        );
    }
}

export class VesselConfigurationV3Factory {
    private engineConfigurationV3Factory = new EngineConfigurationV3Factory();
    private heatingSystemsConfigurationV3Factory = new HeatingSystemsConfigurationV3Factory();

    public create(data: VesselConfigurationV3BM): VesselConfigurationV3 {
        return new VesselConfigurationV3(
            this.engineConfigurationV3Factory.create(data.engineConfiguration),
            this.heatingSystemsConfigurationV3Factory.create(data.heatingSystemsConfiguration),
            data.isEquippedWithPowerLoggingForMainEngine,
            data.isEquippedWithPowerLoggingForAuxiliaryEngine,
            data.propellerType,
            data.isEquippedWithShaftGenerator,
            data.canProduceFreshWaterWithSteam,
            data.isEquippedWithInertGasGenerator,
            data.canProduceFreshWaterByOsmosis,
            data.mustComplyWithMrvRequirements,
            data.loadingComputerType
        );
    }
}
